import React, { useCallback, useState } from "react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Barcode, MagnifyingGlass, WarningCircle, Package, MapPin } from "@phosphor-icons/react";
import {
  productLookup,
  findSkuLocations,
  isEagleInventory,
  normalizeProductMeta,
  stockHealth,
} from "@/lib/inventoryApi";
import CapacityBar from "@/components/inventory/CapacityBar";
import StockMutateForm from "@/components/inventory/StockMutateForm";

// Pull the authoritative stock fields out of a Receive / Pick / Adjust
// response so we can merge them into the in-memory product without a
// re-fetch. Eagle returns per-component counts; regular products return
// `new_stock`.
function pickStockFields(result) {
  const out = {};
  if (result == null) return out;
  if (result.inventory_type === "components") {
    if (result.eagle_frame_stock_quantity != null) out.eagle_frame_stock_quantity = result.eagle_frame_stock_quantity;
    if (result.eagle_door_stock_quantity  != null) out.eagle_door_stock_quantity  = result.eagle_door_stock_quantity;
    if (result.available_complete_sets    != null) out.available_complete_sets    = result.available_complete_sets;
  } else if (result.new_stock != null) {
    out.stock_quantity = result.new_stock;
  }
  return out;
}


/**
 * SKU lookup tab — "where is this SKU stocked, and how much do we have?"
 *
 * Backend has no direct sku→locations endpoint, so we:
 *   1. POST /inventory/lookup { sku } for product metadata + aggregate stock.
 *   2. Fan out GET /inventory/location/{code} across every location whose
 *      cached `current_quantity > 0`, filter to items where product_sku matches.
 *
 * Eagle SKUs (inventory_type === "components") get a dedicated 3-tile card
 * for Frames / Door Sets / Complete Sets and a Component-required mutation
 * form. Regular SKUs render the single-count classic layout.
 */
export default function SkuLookup() {
  const [input, setInput] = useState("");
  const [product, setProduct] = useState(null);
  const [locations, setLocations] = useState(null);
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(null); // { done, total }
  const [error, setError] = useState(null);

  const runLookup = useCallback(async (skuArg, { withLocations = true } = {}) => {
    const sku = (skuArg || "").trim();
    if (!sku) return;
    setLoading(true);
    setError(null);
    try {
      const p = await productLookup(sku);
      setProduct(p);
      if (withLocations) {
        setLocations(null);
        setScanning({ done: 0, total: 0 });
        const found = await findSkuLocations(sku, { onProgress: (s) => setScanning(s) });
        setLocations(found);
      }
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 404) setError(`No product found for SKU "${sku}". Double-check the code.`);
      else setError(detail || err?.message || "Lookup failed");
    } finally {
      setLoading(false);
      setScanning(null);
    }
  }, []);

  const onSubmit = useCallback((e) => {
    e?.preventDefault?.();
    setProduct(null);
    setLocations(null);
    runLookup(input);
  }, [input, runLookup]);

  // Called after a Receive / Pick / Adjust. The mutation response already
  // carries the fresh counts (eagle_frame_stock_quantity / eagle_door_stock_quantity
  // / available_complete_sets, or new_stock for regular SKUs) — merging them
  // in avoids a lagging re-fetch where Base44 hasn't yet reflected the write.
  // On error (409) `result` is undefined; we re-run the full lookup to pull
  // whatever Base44 believes is current so the user sees the truth.
  const refreshAfterMutate = useCallback(({ result } = {}) => {
    if (result && result.success) {
      setProduct((prev) => (prev ? { ...prev, ...pickStockFields(result) } : prev));
    } else if (product?.sku) {
      runLookup(product.sku, { withLocations: false });
    }
  }, [product?.sku, runLookup]);

  return (
    <div className="space-y-4" data-testid="sku-lookup">
      <Card className="p-4">
        <form onSubmit={onSubmit} className="flex flex-wrap items-end gap-2">
          <div className="flex-1 min-w-[220px]">
            <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-zinc-500 mb-1 block">
              SKU
            </label>
            <div className="relative">
              <Barcode size={16} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" />
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="e.g. ESS-WHT-W1236 or EGL-NAT-W2436"
                className="pl-8 h-10 font-mono"
                data-testid="sku-lookup-input"
                autoFocus
              />
            </div>
          </div>
          <Button
            type="submit"
            className="bg-[#C8102E] hover:bg-[#A00D25] h-10"
            disabled={loading || !input.trim()}
            data-testid="sku-lookup-submit"
          >
            <MagnifyingGlass size={16} weight="bold" className="mr-1.5" />
            {loading ? "Searching…" : "Look up"}
          </Button>
        </form>
      </Card>

      {error && (
        <Card className="p-4 border-red-300 bg-red-50 dark:bg-red-950/30 dark:border-red-800" data-testid="sku-lookup-error">
          <div className="flex items-start gap-2">
            <WarningCircle size={20} weight="fill" className="text-red-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-red-800 dark:text-red-200">{error}</div>
          </div>
        </Card>
      )}

      {product && (isEagleInventory(product) ? <EagleProductCard product={product} /> : <ProductCard product={product} />)}

      {product && (
        <StockMutateForm product={product} onDone={({ result, mustRefresh }) => { if (mustRefresh) refreshAfterMutate({ result }); }} />
      )}

      {scanning && (
        <Card className="p-3 text-xs text-zinc-500" data-testid="sku-lookup-scan-progress">
          Scanning bins with stock… {scanning.done} of {scanning.total}
        </Card>
      )}

      {locations && !scanning && <LocationsCard result={locations} sku={product?.sku} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Regular (non-Eagle) product card
// ---------------------------------------------------------------------------

function ProductCard({ product }) {
  const meta = normalizeProductMeta(product);
  const health = stockHealth(product);
  const stockColor =
    health.tone === "red"     ? "text-red-700 dark:text-red-400" :
    health.tone === "amber"   ? "text-amber-700 dark:text-amber-400" :
    health.tone === "green"   ? "text-emerald-700 dark:text-emerald-400" :
                                "text-zinc-500";
  const stock = health.value ?? 0;
  const low = Number(product.low_stock_threshold) || 0;

  return (
    <Card className="p-5" data-testid="sku-lookup-product">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <Package size={20} weight="bold" className="text-zinc-500 flex-shrink-0" />
            <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-semibold">Product</div>
          </div>
          <h2 className="font-mono font-black text-2xl sm:text-3xl mt-1 break-all" data-testid="sku-lookup-product-sku">
            {product.sku}
          </h2>
          <div className="text-lg text-zinc-800 dark:text-zinc-200 mt-1" data-testid="sku-lookup-product-name">
            {meta.name || "—"}
          </div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold mt-1">
            {[product.category, meta.style, meta.finish].filter(Boolean).join(" · ") || "—"}
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-semibold">Total on hand</div>
          <div className={`font-heading font-black text-4xl mt-1 ${stockColor}`} data-testid="sku-lookup-stock">
            {product.track_inventory === false ? "—" : stock}
          </div>
          {health.tone === "amber" && (
            <div className="text-[10px] uppercase tracking-[0.2em] text-amber-700 font-bold mt-1">
              Below threshold ({low})
            </div>
          )}
          {product.track_inventory === false && (
            <div className="text-[10px] text-zinc-400 mt-1">Not tracked</div>
          )}
        </div>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Eagle (frames + doors) product card — 3 tiles, highlighted Complete Sets
// ---------------------------------------------------------------------------

function EagleProductCard({ product }) {
  const meta = normalizeProductMeta(product);
  const health = stockHealth(product);
  const badgeTone =
    health.tone === "red"   ? "bg-red-100 text-red-800 border-red-300 dark:bg-red-950/40 dark:text-red-300 dark:border-red-800" :
    health.tone === "amber" ? "bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-800" :
                              "bg-emerald-100 text-emerald-800 border-emerald-300 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800";

  const dims = [meta.width, meta.height, meta.thickness].filter((v) => v != null && v !== "");
  const dimsText = dims.length ? `${dims.join(' × ')} in` : "";

  return (
    <Card className="p-5 border-indigo-200 dark:border-indigo-900/60" data-testid="sku-lookup-product-eagle">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <Package size={20} weight="bold" className="text-indigo-600 flex-shrink-0" />
            <div className="text-[10px] uppercase tracking-[0.25em] text-indigo-700 dark:text-indigo-300 font-bold">
              Eagle · Frames + Doors tracked separately
            </div>
          </div>
          <h2 className="font-mono font-black text-2xl sm:text-3xl mt-1 break-all" data-testid="eagle-product-sku">
            {product.sku}
          </h2>
          <div className="text-lg text-zinc-800 dark:text-zinc-200 mt-1" data-testid="eagle-product-name">
            {meta.name || "—"}
          </div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold mt-1">
            {[meta.style, meta.finish, dimsText].filter(Boolean).join(" · ") || "—"}
          </div>
        </div>
        <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded border text-[10px] uppercase tracking-[0.15em] font-bold ${badgeTone}`} data-testid="eagle-stock-badge">
          {health.label}
        </div>
      </div>

      {/* 3 tiles — Frames / Door Sets / Complete Sets (highlighted) */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <EagleTile
          label="Frames"
          sublabel="Face-frame fronts"
          value={product.eagle_frame_stock_quantity ?? 0}
          testid="eagle-tile-frames"
        />
        <EagleTile
          label="Door Sets"
          sublabel="Per-cabinet set"
          value={product.eagle_door_stock_quantity ?? 0}
          testid="eagle-tile-doors"
        />
        <EagleTile
          label="Complete Sets"
          sublabel="Shippable cabinets"
          value={product.available_complete_sets ?? 0}
          highlight
          testid="eagle-tile-sets"
        />
      </div>
    </Card>
  );
}

function EagleTile({ label, sublabel, value, highlight, testid }) {
  return (
    <div
      className={[
        "rounded-md border p-4",
        highlight
          ? "border-[#C8102E] bg-[#C8102E]/5"
          : "border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900/40",
      ].join(" ")}
      data-testid={testid}
    >
      <div className={`text-[10px] uppercase tracking-[0.25em] font-bold ${highlight ? "text-[#C8102E]" : "text-zinc-500"}`}>
        {label}
      </div>
      <div className={`font-heading font-black text-4xl mt-1 ${highlight ? "text-[#C8102E]" : "text-zinc-900 dark:text-zinc-100"}`}>
        {value}
      </div>
      <div className="text-[11px] text-zinc-500 mt-0.5">{sublabel}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Locations list — shared across regular + Eagle
// ---------------------------------------------------------------------------

function LocationsCard({ result, sku }) {
  const { results, scanned, total } = result;
  return (
    <Card className="p-5">
      <div className="flex items-baseline justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <MapPin size={18} weight="bold" className="text-zinc-500" />
          <h3 className="font-heading font-black text-lg">Stocked in</h3>
        </div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold" data-testid="sku-locations-summary">
          {results.length} bin{results.length === 1 ? "" : "s"} · scanned {scanned} of {total}
        </div>
      </div>
      {!results.length ? (
        <div className="text-sm text-zinc-500 italic text-center py-8" data-testid="sku-locations-empty">
          {sku ? `SKU "${sku}" isn't currently on hand at any warehouse bin.` : "Not stocked anywhere."}
        </div>
      ) : (
        <div className="border border-zinc-200 dark:border-zinc-800 rounded-md overflow-hidden">
          <table className="w-full text-sm" data-testid="sku-locations-table">
            <thead className="bg-zinc-50 dark:bg-zinc-900 text-[10px] uppercase tracking-[0.15em] font-bold text-zinc-500">
              <tr>
                <th className="text-left py-2 px-3">Location</th>
                <th className="text-left py-2 px-3 hidden sm:table-cell">Zone</th>
                <th className="text-left py-2 px-3 hidden md:table-cell">Bin capacity</th>
                <th className="text-right py-2 px-3">Qty of SKU</th>
              </tr>
            </thead>
            <tbody>
              {results.map(({ location, quantity }) => (
                <tr
                  key={location.location_code}
                  className="border-t border-zinc-200 dark:border-zinc-800"
                  data-testid={`sku-location-${location.location_code}`}
                >
                  <td className="py-2 px-3">
                    <div className="font-mono font-semibold">{location.location_code}</div>
                    <div className="text-xs text-zinc-500">{location.location_name || "—"}</div>
                  </td>
                  <td className="py-2 px-3 hidden sm:table-cell text-[10px] uppercase tracking-[0.15em] text-zinc-500 font-semibold">
                    {location.zone || "—"}
                  </td>
                  <td className="py-2 px-3 hidden md:table-cell">
                    <div className="w-32"><CapacityBar location={location} compact /></div>
                  </td>
                  <td className="py-2 px-3 text-right font-mono font-black text-base">{quantity}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
